import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';

import { Dialog, DialogHeader, DialogBody } from '../ui/Dialog';
import RichTextEditor from '../ui/RichTextEditor';
import { settingsAPI } from '../../services/api';
import { BASE_CUSTOMER_TYPES } from './tcCustomerTypes';
import PageHeader from '../ui/PageHeader';
import { useToast } from '../ui';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

export default function TCTemplatesPage() {
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const res = await settingsAPI.getTCTemplates();
      setTemplates(res || []);
    } catch (err) {
      setError('Failed to load T&C templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleAdd = () => {
    setEditingTemplate(null);
    setModalOpen(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setModalOpen(true);
  };

  const handleDelete = async (template) => {
    try {
      await settingsAPI.deleteTCTemplate(template.id);
      toast.success('Template deleted');
      await fetchTemplates();
    } catch (err) {
      setError('Failed to delete template');
    }
  };

  const handleSave = async (formData) => {
    try {
      if (editingTemplate) {
        await settingsAPI.updateTCTemplate(editingTemplate.id, formData);
        toast.success('Template updated');
      } else {
        await settingsAPI.createTCTemplate(formData);
        toast.success('Template created');
      }
      setModalOpen(false);
      setEditingTemplate(null);
      await fetchTemplates();
    } catch (err) {
      throw new Error(err.message || 'Failed to save template');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="space-y-3"><div className="h-8 rounded animate-pulse" style={{backgroundColor:"var(--neutral-100)"}} /><div className="h-32 rounded animate-pulse" style={{backgroundColor:"var(--neutral-100)"}} /></div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Terms & Conditions Templates"
        subtitle="Create T&C templates that auto-apply based on customer type."
        actions={
          <Button onClick={handleAdd}>
            <PlusIcon className="h-4 w-4 mr-1.5" />
            New Template
          </Button>
        }
      />

      {error && (
        <Alert variant="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {templates.length === 0 ? (
        <Card>
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-gray-500">No T&C templates yet.</p>
            <p className="text-xs text-gray-400 mt-1">
              Templates are auto-applied to quotes based on customer type.
            </p>
            <Button onClick={handleAdd} variant="secondary" size="sm" className="mt-4">
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Create First Template
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {templates.map(template => (
              <li key={template.id} className="px-4 py-4 hover:bg-gray-50 group">
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-gray-900">{template.name}</h3>
                    {(() => {
                      // Iter18 §2.11: multi-select customer_types (with legacy singular fallback).
                      const types = Array.isArray(template.customer_types)
                        ? template.customer_types
                        : template.customer_type ? [template.customer_type] : [];
                      return types.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {types.map((t) => (
                            <span key={t} className="inline-block px-2 py-0.5 bg-gray-100 text-xs text-gray-600 rounded">
                              {t.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                    {template.document ? (
                      <p className="mt-1 text-xs text-gray-500">
                        📎 {template.document.name} · uploaded document
                      </p>
                    ) : template.content && (
                      <p className="mt-1 text-xs text-gray-500 line-clamp-2">
                        {template.content.replace(/<[^>]+>/g, '').slice(0, 150)}...
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-4">
                    <button
                      onClick={() => handleEdit(template)}
                      className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                      aria-label={`Edit ${template.name}`}
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(template)}
                      className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                      aria-label={`Delete ${template.name}`}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Template Editor Modal */}
      <TCEditorModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingTemplate(null); }}
        template={editingTemplate}
        onSave={handleSave}
      />
    </div>
  );
}

// Iter18 §2.11: closed customer-type set from OB-4 wire + "+ Add custom type".
// IDI-626: shared with setup's T&C editor (tcCustomerTypes.js).

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function TCEditorModal({ open, onClose, template, onSave }) {
  const [form, setForm] = useState({
    name: '',
    customer_types: [], // Iter18 §2.11: multi-select.
    content: '',
    document: null, // { name, size, type, dataUrl }
  });
  const [customTypes, setCustomTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      // Backward-compat: old single-string customer_type is coerced into array.
      const legacy = template?.customer_type;
      const initialTypes = Array.isArray(template?.customer_types)
        ? template.customer_types
        : legacy ? [legacy] : [];
      setForm({
        name: template?.name || '',
        customer_types: initialTypes,
        content: template?.content || '',
        document: template?.document || null,
      });
      setError(null);
    }
  }, [open, template]);

  // Ensure any pre-existing custom types are represented as togglable rows.
  const allTypeOptions = React.useMemo(() => {
    const known = new Set([...BASE_CUSTOMER_TYPES, ...customTypes].map((o) => o.value));
    const missing = (form.customer_types || [])
      .filter((v) => !known.has(v))
      .map((v) => ({ value: v, label: v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) }));
    return [...BASE_CUSTOMER_TYPES, ...customTypes, ...missing];
  }, [customTypes, form.customer_types]);

  const toggleType = (value) => {
    setForm((f) => {
      const has = (f.customer_types || []).includes(value);
      return {
        ...f,
        customer_types: has
          ? f.customer_types.filter((v) => v !== value)
          : [...(f.customer_types || []), value],
      };
    });
  };

  const [customTypeInput, setCustomTypeInput] = useState('');
  const addCustomType = () => {
    const label = customTypeInput.trim();
    if (!label) return;
    const value = label.toLowerCase().replace(/\s+/g, '_');
    if (allTypeOptions.some((o) => o.value === value)) {
      // Already exists — just select it.
      if (!(form.customer_types || []).includes(value)) toggleType(value);
    } else {
      setCustomTypes((opts) => [...opts, { value, label }]);
      setForm((f) => ({ ...f, customer_types: [...(f.customer_types || []), value] }));
    }
    setCustomTypeInput('');
  };

  // IDI-649 D6: nothing reads a template's document, so new uploads are
  // gone; a stored document can still be removed.
  const removeDocument = () => setForm((f) => ({ ...f, document: null }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Template name is required');
      return;
    }
    // Iter18 §2.11: relaxed validation — either rich-text content OR an uploaded
    // document is required (one editor, two input paths).
    const hasContent = form.content && form.content.trim() && form.content.trim() !== '<p></p>';
    if (!hasContent && !form.document) {
      setError('Add T&C content in the editor.');
      return;
    }
    try {
      setSaving(true);
      await onSave(form);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="lg">
      <DialogHeader onClose={onClose}>
        {template ? 'Edit T&C Template' : 'New T&C Template'}
      </DialogHeader>
      <DialogBody>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <Alert variant="error">{error}</Alert>}

          <div>
            <label htmlFor="tc-name" className="block text-sm font-medium text-gray-700 mb-1">
              Template Name <span className="text-red-500">*</span>
            </label>
            <Input
              id="tc-name"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Standard Residential T&C"
              required
              autoFocus
            />
          </div>

          {/* Iter18 §2.11: assign by customer TYPE (not job type). Multi-select with
              4 seeded options plus "+ Add custom type". */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Auto-apply to Customer Types
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {allTypeOptions.map((opt) => {
                const selected = (form.customer_types || []).includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleType(opt.value)}
                    className="inline-flex items-center px-2.5 py-1 text-xs font-medium border transition-colors"
                    style={{
                      // Iter18 R2 §2.8: selected = red-filled primary action;
                      // unselected = neutral fill with muted text.
                      backgroundColor: selected ? 'var(--color-action-primary)' : 'var(--neutral-50)',
                      color: selected ? 'var(--white)' : 'var(--color-text-muted)',
                      borderColor: selected ? 'var(--color-action-primary)' : 'var(--color-border)',
                      borderRadius: 'var(--radius-sm)',
                    }}
                    aria-pressed={selected}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2">
              <Input
                value={customTypeInput}
                onChange={(e) => setCustomTypeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomType(); } }}
                placeholder="+ Add custom type"
                className="flex-1"
              />
              <Button type="button" variant="secondary" onClick={addCustomType} disabled={!customTypeInput.trim()}>
                Add
              </Button>
            </div>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              Applies automatically to matching quotes. Leave empty for manual selection.
            </p>
          </div>

          {/* Iter18 R2 §2.8: rich text editor OR uploaded document. Upload moved
              to a secondary CTA BELOW the editor to make the primary write
              path unambiguous. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Terms & Conditions Content
            </label>

            {form.document ? (
              <div
                className="p-3 border"
                style={{
                  backgroundColor: 'var(--neutral-50)',
                  borderColor: 'var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-default)' }}>
                      {form.document.name}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                      {formatBytes(form.document.size)} · {form.document.type === 'application/pdf' ? 'PDF' : 'DOCX'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={removeDocument}
                      className="text-xs font-medium"
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
                  The uploaded document is used at render time. Editor content below is retained but
                  won't appear on quotes while a document is attached.
                </p>
              </div>
            ) : (
              <RichTextEditor
                // Iter18 R2 §2.8: key forces the editor to remount when we
                // switch between templates (or open/close), guaranteeing the
                // seeded form.content lands in the contentEditable DOM.
                key={`tc-rte-${template?.id ?? 'new'}-${open ? 'open' : 'closed'}`}
                value={form.content}
                onChange={(html) => setForm((prev) => ({ ...prev, content: html }))}
                placeholder="Enter your terms and conditions..."
                minHeight={220}
              />
            )}

            <p className="text-xs text-gray-500 mt-1">
              Content appears at the bottom of quotes.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-[color:var(--color-border)]">
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save Template'}
            </Button>
          </div>
        </form>
      </DialogBody>
    </Dialog>
  );
}
